'use client';
import { useEffect, useState } from 'react';
import { todayLocal } from '@/lib/dates';
import { marReadWindow } from '@/lib/medications/mar-read-window';

/** Refresh against adjacent named-clock midnights, including short/long days. */
export function useMedicationChartDate(timezone: string): string {
  const [clock, setClock] = useState(() => ({ timezone, date: todayLocal(timezone) }));
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    const refresh = () => {
      clearTimeout(timer);
      const date = todayLocal(timezone);
      setClock((current) =>
        current.timezone === timezone && current.date === date ? current : { timezone, date },
      );
      const nextMidnight = Date.parse(marReadWindow(date, timezone).endUtcExclusive);
      // Recheck long-lived tabs after clock adjustments as well as at midnight.
      timer = setTimeout(refresh, Math.max(1, Math.min(nextMidnight - Date.now(), 3_600_000)));
    };
    const visible = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    refresh();
    document.addEventListener('visibilitychange', visible);
    window.addEventListener('focus', refresh);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', visible);
      window.removeEventListener('focus', refresh);
    };
  }, [timezone]);
  return clock.timezone === timezone ? clock.date : todayLocal(timezone);
}
